import {
  Badge,
  BodyText,
  Button,
  Card,
  HeaderText,
  Search,
} from "@/components/ui";
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Condition } from "fhir/r4";
import {
  GripVertical,
  ListPlus,
  SearchIcon,
  Stethoscope,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { type UseFormReturn, useFieldArray } from "react-hook-form";
import { Icd10QuickActions } from "../Icd10QuickActions";
import { useIcd10CodeSearch } from "../network";
import { toIcd10Coding } from "../quickActions";
import type { VisitClosureFormData, VisitClosureFormInput } from "../schemas";

type SelectedCode = VisitClosureFormData["dxBillingCodes"][number];

interface OptionMetadata {
  system: string;
  version: string;
  term: string;
}

interface DxBillingCodesStepProps {
  form: UseFormReturn<VisitClosureFormInput, unknown, VisitClosureFormData>;
  patientId: string;
  visitDiagnoses: Condition[];
}

const rankLabels = ["Primary", "Secondary", "Tertiary"] as const;

const SortableCodeRow = ({
  item,
  index,
  onRemove,
}: {
  item: SelectedCode;
  index: number;
  onRemove: (code: string) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.code });

  return (
    <Card
      className={`flex items-center gap-2.5 rounded-md border border-primary bg-white px-3 py-2.5 ${isDragging ? "z-10 opacity-50" : ""}`}
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <Button
        aria-label={`Drag ${item.code} to reorder`}
        className="relative flex h-[14px] w-[14px] shrink-0 cursor-grab touch-none items-center justify-center text-disabled before:absolute before:-inset-[7px] before:rounded-full hover:text-tertiary active:cursor-grabbing"
        size="snatched"
        title="Drag to reorder"
        variant="icon"
        {...attributes}
        {...listeners}
      >
        <GripVertical aria-hidden size={14} />
      </Button>
      <BodyText
        bold
        className="shrink-0 text-[13.5px]"
        size="sm"
        textColor="link"
      >
        {item.code}
      </BodyText>
      <BodyText
        className="min-w-0 flex-1 text-[13px]"
        size="sm"
        textColor="secondary"
      >
        {item.display}
      </BodyText>
      {rankLabels[index] && (
        <Badge
          className={`shrink-0 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.03em] ${index === 0 ? "bg-link-subtle text-link" : index === 1 ? "bg-info-subtle text-info" : "bg-tertiary text-secondary"}`}
        >
          {rankLabels[index]}
        </Badge>
      )}
      <Button
        aria-label={`Remove ${item.code}`}
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-tertiary hover:bg-secondary hover:text-error"
        onClick={() => onRemove(item.code)}
        size="snatched"
        variant="icon"
      >
        <Trash2 size={15} />
      </Button>
    </Card>
  );
};

export const DxBillingCodesStep = ({
  form,
  patientId,
  visitDiagnoses,
}: DxBillingCodesStepProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: "dxBillingCodes",
  });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const { data, isLoading } = useIcd10CodeSearch(patientId, searchTerm);

  useEffect(() => {
    void form.trigger("dxBillingCodes");
  }, [form]);

  const options =
    data?.matchedConcepts
      ?.filter(
        (result) =>
          !(form.getValues("dxBillingCodes") ?? []).some(
            (selected) => selected.code === result.id,
          ),
      )
      .map((result) => ({
        label: `${result.id} - ${result.term}`,
        disabled: result.billable !== undefined && !result.billable,
        level: result.level,
        value: result.id,
        metadata: {
          system: result.system,
          version: result.version,
          term: result.term,
        } satisfies OptionMetadata,
      })) ?? [];

  const appendCode = async (code: SelectedCode) => {
    if (
      (form.getValues("dxBillingCodes") ?? []).some(
        (selected) => selected.code === code.code,
      )
    ) {
      return;
    }

    append(code, { shouldFocus: false });
    await form.trigger("dxBillingCodes");
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    const currentCodes = form.getValues("dxBillingCodes") ?? [];
    const fromIndex = currentCodes.findIndex(({ code }) => code === active.id);
    const toIndex = currentCodes.findIndex(({ code }) => code === over.id);
    if (fromIndex < 0 || toIndex < 0) {
      return;
    }

    move(fromIndex, toIndex);
    void form.trigger("dxBillingCodes");
  };

  return (
    <div className="flex flex-col gap-[14px]">
      <HeaderText size="md">Diagnoses &amp; Billing Codes</HeaderText>
      <div className="flex flex-row items-start gap-[18px] max-[560px]:flex-col">
        <div className="sticky top-0 w-[220px] shrink-0 rounded-md bg-secondary px-4 py-[14px] max-[560px]:static max-[560px]:w-full">
          <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.05em] text-tertiary">
            <Stethoscope size={14} /> Visit diagnoses
          </div>
          {visitDiagnoses.map((diagnosis, index) => {
            const display =
              diagnosis.code?.text ||
              diagnosis.code?.coding?.find((coding) => coding.display)
                ?.display ||
              "Unknown diagnosis";
            return (
              <div
                className={`py-[9px] text-[13.5px] font-semibold text-primary ${index === 0 ? "" : "border-primary border-t"}`}
                key={diagnosis.id ?? `${display}-${index}`}
              >
                {display}
              </div>
            );
          })}
        </div>

        <div className="min-w-0 flex-1 max-[560px]:w-full">
          <div className="mb-2 flex items-center">
            <span className="text-[11px] font-bold uppercase tracking-[0.05em] text-tertiary">
              Billing codes for this claim
            </span>
          </div>

          <div className="mb-[14px] flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <Search
                aria-label="Search ICD-10 codes to add"
                clearOnSelect
                input={searchTerm}
                isLoading={isLoading}
                leftIcon={SearchIcon}
                minLength={3}
                noResultsText={
                  searchTerm.length < 3
                    ? "Enter at least 3 characters to search."
                    : "No matching ICD-10 codes"
                }
                onInputChange={setSearchTerm}
                onItemSelected={(item) => {
                  const metadata = item.metadata as OptionMetadata;
                  void appendCode({
                    code: item.value,
                    display: metadata.term,
                    system: metadata.system,
                    version: metadata.version,
                  });
                  setSearchTerm("");
                }}
                options={options}
                placeholder="Search ICD-10 codes to add…"
                showLoadingSpinner
              />
            </div>
            <Icd10QuickActions
              appliedCodes={fields.map(({ code }) => code)}
              onSelect={(action) => {
                const coding = toIcd10Coding(action);
                void appendCode({
                  code: coding.code,
                  display: coding.display,
                  system: coding.system,
                  version: coding.version,
                });
              }}
              triggerTooltip="Quick add ICD-10 code"
              trigger={
                <Button
                  aria-label="Quick add ICD-10 code"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-secondary bg-white text-link hover:bg-secondary"
                  size="snatched"
                  type="button"
                  variant="icon"
                >
                  <ListPlus aria-hidden size={16} />
                </Button>
              }
            />
          </div>

          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            sensors={sensors}
          >
            <SortableContext
              items={fields.map(({ code }) => code)}
              strategy={verticalListSortingStrategy}
            >
              <div className="flex flex-col gap-2">
                {fields.map((item, index) => (
                  <SortableCodeRow
                    index={index}
                    item={item}
                    key={item.code}
                    onRemove={(code) => {
                      const removeIndex = (
                        form.getValues("dxBillingCodes") ?? []
                      ).findIndex((item) => item.code === code);
                      if (removeIndex < 0) {
                        return;
                      }

                      remove(removeIndex);
                      void form.trigger("dxBillingCodes");
                    }}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {!fields.length && (
            <BodyText
              className="my-[12.5px] rounded-md border border-dashed border-secondary p-4 text-center"
              size="xs"
              tag="p"
              textColor="tertiary"
            >
              No billing codes added yet — search above to add at least one.
            </BodyText>
          )}
        </div>
      </div>
    </div>
  );
};
